import React, { useState } from "react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts"
import { useCasesTimeSeries, TimePoint } from "@/hooks/useCasesTimeSeries"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

type IntervalType = "day" | "week" | "month"

interface CasesTimeSeriesChartProps {
  className?: string
}

export const CasesTimeSeriesChart: React.FC<CasesTimeSeriesChartProps> = ({ className }) => {
  const [interval, setInterval] = useState<IntervalType>("week")
  const [limit, setLimit] = useState(12)
  
  const { data, isLoading, error } = useCasesTimeSeries(interval, limit)
  
  const getLimitOptions = (interval: IntervalType) => {
    switch (interval) {
      case "day":
        return [
          { value: 7, label: "Last 7 days" },
          { value: 14, label: "Last 14 days" },
          { value: 30, label: "Last 30 days" },
        ]
      case "week":
        return [
          { value: 4, label: "Last 4 weeks" },
          { value: 8, label: "Last 8 weeks" },
          { value: 12, label: "Last 12 weeks" },
        ]
      case "month":
        return [
          { value: 6, label: "Last 6 months" },
          { value: 12, label: "Last 12 months" },
          { value: 24, label: "Last 24 months" },
        ]
    }
  }
  
  // Handle interval change
  const handleIntervalChange = (newInterval: string) => {
    const typedInterval = newInterval as IntervalType
    setInterval(typedInterval)
    
    // Reset limit to a sensible default for the new interval
    switch (typedInterval) {
      case "day":
        setLimit(14)
        break
      case "week":
        setLimit(12)
        break
      case "month":
        setLimit(12)
        break
    }
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Cases Created Over Time</CardTitle>
        </CardHeader>
        <CardContent className="h-80 flex items-center justify-center">
          <p className="text-muted-foreground">Loading chart data...</p>
        </CardContent>
      </Card>
    )
  }

  if (error || !data) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Cases Created Over Time</CardTitle>
        </CardHeader>
        <CardContent className="h-80 flex items-center justify-center">
          <p className="text-destructive">Error loading chart data</p>
        </CardContent>
      </Card>
    )
  }

  const formatXAxisTick = (value: string) => {
    const date = new Date(value)
    switch (interval) {
      case "day":
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      case "week":
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      case "month":
        return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
    }
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Cases Created Over Time</CardTitle>
        <div className="flex items-center space-x-2">
          <Select
            value={interval}
            onValueChange={handleIntervalChange}
          >
            <SelectTrigger className="w-[110px]">
              <SelectValue placeholder="Interval" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Daily</SelectItem>
              <SelectItem value="week">Weekly</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
            </SelectContent>
          </Select>
          
          <Select
            value={limit.toString()}
            onValueChange={(value) => setLimit(parseInt(value))}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Time range" />
            </SelectTrigger>
            <SelectContent>
              {getLimitOptions(interval).map((option) => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data.data_points}
              margin={{
                top: 10,
                right: 30,
                left: 20,
                bottom: 30,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatXAxisTick}
                angle={-30}
                textAnchor="end"
                height={50}
              />
              <YAxis allowDecimals={false} />
              <Tooltip 
                formatter={(value) => [`${value} cases`, "Cases"]}
                labelFormatter={(label) => {
                  const date = new Date(label)
                  return date.toLocaleDateString(undefined, { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                name="Cases"
                stroke="#4f46e5"
                activeDot={{ r: 8 }}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
} 